import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { intVal, Nullable } from '@jamesgmarks/utilities';
import { useAppSelector } from 'src/redux/hooks';
import { ICommissionSchemeData } from '../EditorComponents/CommissionEditor/CommissionEditor';
import { TWhereCondition } from "@llws/dynamic-router-utils";
import { createWhereCommand } from 'src/app-utils/helpers';
import {
  Grid,
  Paper,
  Fade,
  Slide,
  Box,
  Typography,
  Divider,
  Stack,
  Tooltip,
  Fab,
  GlobalStyles,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PreviewIcon from '@mui/icons-material/Preview';
import LinkIcon from '@mui/icons-material/Link';
import {
  assertValidBillingFrequency,
  assertValidBillingType,
  BILLING_FEATURE_MANAGED_EXTERNALLY,
  BILLING_FEATURE_SUPERSYNC_URL,
  billingTypeHasFeature,
  getBillingFeatureMetadata,
  getBillingFrequencies,
  TValidBillingFeature,
} from '@llws/hydra-shared';
import { FloatingFileViewer } from 'src/components/pages/Subscriptions/EditorComponents/FileUpload/FloatingFileViewer';
import {
  buildSubscriptionCommandPayload,
} from 'src/components/pages/Subscriptions/form/buildSubscriptionCommandPayload';
import { REACT_APP_API_ROOT_URI } from 'src/App';
import { apiFetch } from 'src/redux/utils';
import {
  SubscriptionBaseDetails,
  SubscriptionInvoiceFields,
  SubscriptionDates,
  SubscriptionPricing,
  SubscriptionAccountingSales,
  SubscriptionMiscellaneous,
  SubscriptionAdvancedFeatures,
  SubscriptionAdditionalInfo,
  TJsonNoteWithFlag,
} from './components';
import { createNote, getDefaultRateExpiryDateFromSubscriptionStartDate } from './utils';
import {
  useSubscriptionData,
  useCommissionScheme,
  useVBPData,
  useSubscriptionFormData,
  useNotesManager,
  useDefaultCommissionForSalesRep,
} from './hooks';
import { globalAnimationKeyframes } from 'src/components/pages/Subscriptions/form/animationStyles';
import { useFormChangeTracker } from './hooks/useFormChangeTracker';
import { SaveConfirmationModal } from './components/SaveConfirmationModal';
import { useAuth } from 'src/customHooks/useAuth';

const SubscriptionForm: React.FC = () => {
  const { subscriptionId: _subscriptionId } = useParams<{ subscriptionId?: string; }>();

  const subscriptionId = /^[0-9]+$/.test(_subscriptionId ?? '') ? intVal(_subscriptionId) : null;

  const userId = useAppSelector(state => state.auth?.decodedToken?.userId);
  const userName = useAppSelector(state => state.auth?.decodedToken?.name);

  const history = useHistory();

  const storeSubscription = (
    useAppSelector((store) => store.dynamic.data.subscriptions?.single)
  );

  const availableSalesReps = (
    useAppSelector((store) => store.dynamic.data.sales_reps?.list) ?? []
  );

  const availableBillingFrequencies = (
    useAppSelector((store) => store.dynamic.data.billing_frequencies?.list) ?? []
  );

  // auth context
  const { permissions } = useAuth();

  // Use custom hooks for data management
  const { fetchCommissionSchemes } = useSubscriptionData(subscriptionId);
  const { commissionSchemeData, setCommissionSchemeData } = useCommissionScheme(storeSubscription, subscriptionId);
  const {
    subscriptionData,
    setSubscriptionData,
    discountRateFormValue,
    setDiscountRateFormValue,
    commissionRateFormValue,
    setCommissionRateFormValue,
    selectedBaseSubscription,
    setSelectedBaseSubscription,
  } = useSubscriptionFormData(storeSubscription);
  const { vBPData, defaultVBPData, setVBPData } = useVBPData(storeSubscription, selectedBaseSubscription);
  const { subscriptionNotes, newNotes, onDeleteNote, onAddNote } = useNotesManager(subscriptionData, userId, userName);
  const { fetchDefaultCommissionForSalesRep } = useDefaultCommissionForSalesRep(
    subscriptionData.salesRepId ?? null, subscriptionData.baseSubscriptionId ?? null,
  );

  // Change tracking for note requirements
  const isDataReady = useMemo(() => {
    if (!subscriptionId) return true;
    return !!storeSubscription && !!subscriptionData.baseSubscriptionId;
  }, [subscriptionId, storeSubscription, subscriptionData.baseSubscriptionId]);

  const changeTracker = useFormChangeTracker({
    currentData: subscriptionData,
    hasNewNote: newNotes.length > 0,
    isDataLoaded: isDataReady,
    isCreateMode: !subscriptionId,
  });

  // Local state for other components
  const [uploadedFile, setUploadedFile] = useState<Nullable<File>>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedClientSalesRepId, setSelectedClientSalesRepId] = (
    useState<Nullable<number>>(storeSubscription?.client?.salesRepId ?? null));
  const [
    isEditingSubscriptionCommission,
    setIsEditingSubscriptionCommission,
  ] = useState(false);

  // Commission Permissions state
  const [ hasCommissionPermissions ] = useState((
    permissions.FULL_ACCESS === 'grant'
    || permissions.READ_ALL_COMMISSIONS === 'grant'
    || permissions.READ_OWN_COMMISSIONS === 'grant'
  ));

  const onBuildingChanged = useCallback(
    (building, price) => {
      setSubscriptionData((old) => ({
        ...old,
        buildingsData: [
          ...old.buildingsData.filter(({ id }) => id !== building.id),
          {
            id: building.id,
            name: building.buildingName,
            price,
          },
        ],
      }));
    }, [setSubscriptionData],
  );

  const onBuildingRemoved = useCallback(
    (id) => {
      setSubscriptionData((old) => ({
        ...old,
        buildingsData: old.buildingsData.filter((b) => b.id !== id),
      }));
    }, [setSubscriptionData],
  );

  const billingFeatureMetadata = useCallback(
    (billingTypeId: number, feature: TValidBillingFeature, field: string) => {
      const metadata = getBillingFeatureMetadata(
        assertValidBillingType(billingTypeId),
        // TODO: replace with a more type-safe approach using hydra shared
        // @ts-ignore: Type compatibility issue between TValidBillingFeature and TBillingFeatureID
        feature,
      ) as Record<string, unknown>;
      return metadata[field];
    },
    [],
  );

  const billingTypeId = useMemo(
    () => (selectedBaseSubscription?.service?.billingTypeId ?? 1), [selectedBaseSubscription],
  );

  const isCreateMode = useMemo(() => !subscriptionId, [subscriptionId]);

  const billingFrequenciesAllowed = useMemo(() => {
    if (availableBillingFrequencies.length !== 0) {
      const frequenciesForBillingType = getBillingFrequencies(billingTypeId);
      const allowedFrequencesForBillingType = availableBillingFrequencies.filter(
        (bf) => (
          bf.id && frequenciesForBillingType.includes(assertValidBillingFrequency(bf.id))
        ),
      );
      if (allowedFrequencesForBillingType.length !== frequenciesForBillingType.length) {
        //TODO: replace with a more user-friendly error handling
        throw new Error(
          `Billing Type ${billingTypeId} has invalid billing frequencies: ${frequenciesForBillingType.join(', ')}`,
        );
      }
      return allowedFrequencesForBillingType;
    }
  }, [billingTypeId, availableBillingFrequencies]);

  const onUpdateCommissionScheme = useCallback(
    (updatedCommissionSchemeData: ICommissionSchemeData) => {
      setCommissionSchemeData(updatedCommissionSchemeData);
    }, [setCommissionSchemeData],
  );

  const baseSubscriptionQueryFilters = useMemo(
    () => {
      if (subscriptionId) {
        return [
          createWhereCommand(
            'id',
            `${subscriptionData.baseSubscriptionId}`,
            undefined,
            'or' as TWhereCondition,
          ),
        ];
      }
      return [
        createWhereCommand(
          'serviceId',
          'null',
          'null',
          undefined,
          true,
        ),
      ];
    }, [subscriptionId, subscriptionData.baseSubscriptionId],
  );

  const handleSaveAttempt = () => {
    const { hasChanges, canSave } = changeTracker.checkForChanges();

    if (hasChanges && !canSave) {
      changeTracker.triggerNoteRequirement();
      setSaveModalOpen(true);
    } else {
      handleSave();
    }
  };

  const handleSaveWithNote = async (note: string) => {
    const noteCreated = createNote(note, userId, userName);
    setSaveModalOpen(false);

    handleSave(noteCreated);
  };

  const handleSave = async (note?: TJsonNoteWithFlag) => {
    setIsSubmitting(true);

    try {
      const commandPayload = buildSubscriptionCommandPayload({
        subscriptionData,
        commissionSchemeData,
        vBPData,
        notesData: note ? [note, ...newNotes] : newNotes,
        storeSubscription,
      });

      const url = `${REACT_APP_API_ROOT_URI}/lift_subscriptions/hydra/`;
      const method = 'POST';
      const response = await apiFetch(url, {
        method,
        body: JSON.stringify(commandPayload),
      });
      if (response.ok) {
        // Reset baseline after successful save
        changeTracker.resetBaseline();
        const responseData = await response.json();
        storeSubscription?.id ? window.location.reload() : history.push(`/subscriptions/form/${responseData.id}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (
    !isCreateMode
    && billingTypeHasFeature(
      assertValidBillingType(billingTypeId),
      BILLING_FEATURE_MANAGED_EXTERNALLY,
    )
  ) {
    return <Grid container spacing={2} mt={2} mr={2} justifyContent="center" alignItems="center">
      <Grid item xs={10}>
        <Paper variant='outlined' sx={{ padding: '1rem', backgroundColor: '#e0e0e0' }}>
          <Grid item xs={12} style={{ textAlign: 'center' }}>
            <p> This feature is managed somewhere else</p>
            {
              billingTypeHasFeature(assertValidBillingType(billingTypeId), BILLING_FEATURE_SUPERSYNC_URL)
              && <>

                <a href={`${billingFeatureMetadata(billingTypeId, BILLING_FEATURE_SUPERSYNC_URL, 'url')}`}>
                  Go to SuperSync
                </a>
              </>
            }
          </Grid>
        </Paper>
      </Grid>
    </Grid>;
  }

  return (
    <Box component="form" sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e2e8f0', mb: 3 }}>
        <Box maxWidth={1000} mx="auto" px={3} py={4}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1e293b', textAlign: 'center' }}>
            {subscriptionId ? `Edit Subscription (${subscriptionId})` : 'Create Subscription'}
          </Typography>
        </Box>
      </Box>

      {/* {uploadedFile && modalOpen && (
        <FloatingFileViewer file={uploadedFile} onClose={() => setModalOpen(false)} />
      )} */}

      {/* Main Form Container */}
      <Box maxWidth={1000} mx="auto" px={3} pb={5}>
        <GlobalStyles styles={globalAnimationKeyframes} />
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box p={4}>
            <Grid container spacing={4}>
              {/* Base Details Section */}
              <Grid item xs={12}>
                <Box mb={3}>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: '#374151', mb: 3 }}>
                    Basic Information
                  </Typography>
                  <SubscriptionBaseDetails
                    subscriptionData={subscriptionData}
                    setSubscriptionData={setSubscriptionData}
                    setSelectedBaseSubscription={setSelectedBaseSubscription}
                    setSelectedClientSalesRepId={setSelectedClientSalesRepId}
                    setCommissionSchemeData={setCommissionSchemeData}
                    baseSubscriptionQueryFilters={baseSubscriptionQueryFilters}
                    isCreateMode={isCreateMode}
                  />
                </Box>
              </Grid>

              <Fade in={!!subscriptionData.baseSubscriptionId} timeout={500}>
                <Grid item xs={12}>
                  <Slide direction="up" in={!!subscriptionData.baseSubscriptionId} timeout={400}>
                    <Box>
                      {!!subscriptionData.baseSubscriptionId && (
                        <Grid container spacing={4}>
                          {/* Invoice Section */}
                          <Grid item xs={12}>
                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: '#374151', mb: 3 }}>
                              Invoice Details
                            </Typography>
                            <SubscriptionInvoiceFields
                              subscriptionData={subscriptionData}
                              setSubscriptionData={setSubscriptionData}
                            />
                          </Grid>

                          {/* Dates Section */}
                          <Grid item xs={12}>
                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: '#374151', mb: 3 }}>
                              Subscription Timeline
                            </Typography>
                            <SubscriptionDates
                              subscriptionData={subscriptionData}
                              setSubscriptionData={setSubscriptionData}
                              getDefaultRateExpiryDateFromSubscriptionStartDate={
                                getDefaultRateExpiryDateFromSubscriptionStartDate
                              }
                            />
                          </Grid>

                          {/* Accounting & Sales Section */}
                          <Grid item xs={12}>
                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: '#374151', mb: 3 }}>
                              Accounting & Sales Info
                            </Typography>
                            <SubscriptionAccountingSales
                              subscriptionData={subscriptionData}
                              commissionRateFormValue={commissionRateFormValue}
                              setCommissionRateFormValue={setCommissionRateFormValue}
                              defaultCommissionForSalesRep={fetchDefaultCommissionForSalesRep}
                              availableSalesReps={availableSalesReps}
                              availableBillingFrequencies={billingFrequenciesAllowed ?? []}
                              hasCommissionPermissions={hasCommissionPermissions}
                              setSubscriptionData={setSubscriptionData}
                            />
                          </Grid>

                          {/* Miscellaneous Section */}
                          <Grid item xs={12}>
                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: '#374151', mb: 3 }}>
                              Miscellaneous
                            </Typography>
                            <SubscriptionMiscellaneous
                              subscriptionData={subscriptionData}
                              setSubscriptionData={setSubscriptionData}
                            />
                          </Grid>

                          {/* Pricing Section */}
                          <Grid item xs={12}>
                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: '#374151', mb: 3 }}>
                              Pricing
                            </Typography>
                            <SubscriptionPricing
                              subscriptionData={subscriptionData}
                              setSubscriptionData={setSubscriptionData}
                              discountRateFormValue={discountRateFormValue}
                              setDiscountRateFormValue={setDiscountRateFormValue}
                              billingTypeId={billingTypeId}
                              billingFeatureMetadata={billingFeatureMetadata}
                            />
                          </Grid>

                          {/* Advanced Features Section */}
                          <Grid item xs={12}>
                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: '#374151', mb: 3 }}>
                              Advanced Features
                            </Typography>
                            <SubscriptionAdvancedFeatures
                              subscriptionData={subscriptionData}
                              setSubscriptionData={setSubscriptionData}
                              billingTypeId={billingTypeId}
                              billingFeatureMetadata={billingFeatureMetadata}
                              onBuildingChanged={onBuildingChanged}
                              onBuildingRemoved={onBuildingRemoved}
                              vBPData={vBPData}
                              setVBPData={setVBPData}
                              defaultVBPData={defaultVBPData}
                              selectedBaseSubscription={selectedBaseSubscription}
                              storeSubscription={storeSubscription}
                              isEditingSubscriptionCommission={isEditingSubscriptionCommission}
                              setIsEditingSubscriptionCommission={setIsEditingSubscriptionCommission}
                              subscriptionId={subscriptionId}
                              commissionSchemeData={commissionSchemeData}
                              selectedClientSalesRepId={selectedClientSalesRepId}
                              onUpdateCommissionScheme={onUpdateCommissionScheme}
                              fetchCommissionSchemes={fetchCommissionSchemes}
                            />
                          </Grid>

                          {/* Additional Info Section */}
                          <Grid item xs={12}>
                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: '#374151', mb: 3 }}>
                              Additional Info
                            </Typography>
                            <SubscriptionAdditionalInfo
                              subscriptionNotes={subscriptionNotes}
                              userId={userId}
                              onDeleteNote={onDeleteNote}
                              onAddNote={onAddNote}
                              setUploadedFile={setUploadedFile}
                              requiresNote={changeTracker.requiresNote}
                              hasChanges={changeTracker.hasLikelyChanges()}
                              changedFields={changeTracker.getChangesSummary()}
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </Slide>
                </Grid>
              </Fade>
            </Grid>
          </Box>
        </Paper>
      </Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: 'fixed',
          top: 84,
          right: 24,
          zIndex: 4000,
        }}
      >
        {/* <Tooltip title="View File" arrow>
          <span>
            <Fab
              color="secondary"
              aria-label="preview"
              onClick={() => setModalOpen(prev => !prev)}
              disabled={!uploadedFile}
            >
              <PreviewIcon />
            </Fab>
          </span>
        </Tooltip> */}
        <Tooltip title="Go to supersync" arrow>
          <span>
            <Fab
              color="primary"
              aria-label="link to superlift"
              onClick={() => {
                if (storeSubscription?.id) {
                  window.open(
                    `https://superlift.theliftsystem.com/subscriptions/view/${storeSubscription.id}`,
                    '_blank',
                  );
                }
              }}
              disabled={!storeSubscription?.id}
            >
              <LinkIcon />
            </Fab>
          </span>
        </Tooltip>
        <Tooltip title={subscriptionId ? 'Save' : 'Create'} arrow>
          <span>
            <Fab
              color="success"
              aria-label="save"
              onClick={handleSaveAttempt}
              disabled={isSubmitting}
            >
              <SaveIcon />
            </Fab>
          </span>
        </Tooltip>
      </Stack>

      {/* Save Confirmation Modal */}
      <SaveConfirmationModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onSave={handleSaveWithNote}
        changedFields={changeTracker.getChangesSummary()}
        hasChanges={changeTracker.hasLikelyChanges()}
      />
    </Box>
  );
};

export default SubscriptionForm;
